import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftIcon, ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Textarea } from '../UI/Textarea';
import { Label } from '../UI/Label';
import { Alert, AlertDescription } from '../UI/Alert';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../UI/Select';
import LoadingSpinner from '../UI/LoadingSpinner';
import {
  TicketStatus,
  TicketPriority,
  TicketCategory,
  CreateTicketRequest,
  UpdateTicketRequest,
  Ticket,
  TicketAttachment
} from '../../types/ticket';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, usersService, User } from '../../services/users';
import { ticketService } from '../../services/tickets';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import { filesFromClipboardEvent, validateFiles, placeholdersForPendingImages, MAX_FILE_SIZE_BYTES } from '../../utils/imageUpload';

interface TicketFormProps {
  ticket?: Ticket;
  onSave: (data: CreateTicketRequest | UpdateTicketRequest) => Promise<Ticket | null>;
  onCancel: () => void;
  loading?: boolean;
}

export const TicketForm: React.FC<TicketFormProps> = ({
  ticket,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: TicketPriority.MEDIUM,
    category: TicketCategory.TECHNICAL_SUPPORT,
    status: TicketStatus.OPEN,
    assigned_to_id: '',
    company_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [assignedInput, setAssignedInput] = useState<string>('');
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const [pendingImages, setPendingImages] = useState<{ id: string, file: File, alt: string }[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const assigneeBoxRef = useRef<HTMLDivElement | null>(null);

  // Verificar se o usuário é CLIENT
  const isClientUser = user?.role === UserRole.CLIENT;

  useEffect(() => {
    if (ticket) {
      setFormData({
        title: ticket.title,
        description: ticket.description,
        priority: ticket.priority,
        category: ticket.category,
        status: ticket.status,
        assigned_to_id: ticket.assigned_to?.id?.toString() || '',
        company_id: ticket.company?.id?.toString() || '',
      });
      if (ticket.assigned_to) {
        setAssignedInput(ticket.assigned_to.full_name ? `${ticket.assigned_to.full_name} (${ticket.assigned_to.email})` : ticket.assigned_to.email);
      }
    } else if (isClientUser && user?.company_id) {
      // Para usuários CLIENT, definir automaticamente a empresa
      setFormData(prev => ({
        ...prev,
        company_id: user.company_id!.toString(),
      }));
    }
  }, [ticket, isClientUser, user]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        setUsersError(null);
        const analysts = await usersService.getAnalysts();
        setUsers(analysts || []);
      } catch (err: any) {
        console.error('Erro ao carregar usuários:', err);
        setUsersError(t('Error loading users'));
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [t]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (assigneeBoxRef.current && !assigneeBoxRef.current.contains(e.target as Node)) {
        setAssigneeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = t('Title is required');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('Description is required');
    }

    if (!ticket && !formData.company_id && !isClientUser) {
      newErrors.company_id = t('Company is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const submitData: CreateTicketRequest | UpdateTicketRequest = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        category: formData.category,
        status: formData.status,
        assigned_to_id: formData.assigned_to_id ? parseInt(formData.assigned_to_id) : undefined,
        ...(ticket ? {} : { company_id: parseInt(formData.company_id) })
      };

      const savedTicket = await onSave(submitData);

      // Se houver imagens pendentes no modo criação, enviar e atualizar descrição
      if (savedTicket && pendingImages.length > 0) {
        try {
          const files = pendingImages.map(p => p.file);
          const uploaded = await ticketService.uploadAttachments(savedTicket.id, files);
          const makeFullUrl = (u: string) => u.startsWith('http') ? u : `${api.defaults.baseURL}${u}`;
          const fullUrls: string[] = uploaded.map((a: any) => makeFullUrl(a.url));
          let updatedDescription = formData.description;
          pendingImages.forEach((p, i) => {
            updatedDescription = updatedDescription.split(`pending:${p.id}`).join(fullUrls[i]);
          });
          await ticketService.updateTicket(savedTicket.id, { description: updatedDescription });
        } catch (err) {
          console.error('Falha ao enviar imagens e atualizar descrição:', err);
        } finally {
          setPendingImages([]);
        }
      }

      setSaving(false);

      if (savedTicket) {
        onCancel();
      }
    } catch (err) {
      console.error('Falha ao salvar ticket:', err);
      setSaving(false);
    }
  };

  const isEditing = !!ticket;
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const insertAtCursor = (text: string) => {
    const el = descriptionRef.current;
    if (!el) return;
    const start = el.selectionStart ?? formData.description.length;
    const end = el.selectionEnd ?? start;
    const before = formData.description.slice(0, start);
    const after = formData.description.slice(end);
    const newVal = `${before}${text}${after}`;
    setFormData(prev => ({ ...prev, description: newVal }));
    setTimeout(() => {
      const cursorPos = start + text.length;
      el.setSelectionRange(cursorPos, cursorPos);
      el.focus();
    }, 0);
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesList = e.target.files;
    if (!filesList || filesList.length === 0) return;

    const { valid, errors } = validateFiles(Array.from(filesList), { acceptImagesOnly: true, maxSizeBytes: MAX_FILE_SIZE_BYTES });
    setAttachmentError(errors.length ? errors.join('\n') : null);
    if (valid.length === 0) {
      e.target.value = '';
      return;
    }

    try {
      if (isEditing && ticket) {
        const attachments = await ticketService.uploadAttachments(ticket.id, valid);
        const mdLinks = attachments.map(att => {
          const link = att.download_url || att.url;
          return `![${att.filename}](${link})`;
        }).join('\n');
        insertAtCursor(`\n\n${mdLinks}`);
      } else {
        const { pending, markdown } = placeholdersForPendingImages(valid);
        setPendingImages(prev => [...prev, ...pending]);
        insertAtCursor(`\n\n${markdown}`);
      }
    } catch (error) {
      console.error('Falha ao enviar anexos', error);
    } finally {
      e.target.value = '';
    }
  };

  const handleInsertImage = () => {
    fileInputRef.current?.click();
  };

  const handleDescriptionPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = filesFromClipboardEvent(e);
    if (files.length === 0) return;

    const hasText = (e.clipboardData?.getData('text') || '').length > 0;
    if (!hasText) e.preventDefault();

    const { valid, errors } = validateFiles(files, { acceptImagesOnly: true, maxSizeBytes: MAX_FILE_SIZE_BYTES });
    setAttachmentError(errors.length ? errors.join('\n') : null);
    if (valid.length === 0) return;

    try {
      if (isEditing && ticket) {
        const attachments: TicketAttachment[] = await ticketService.uploadAttachments(ticket.id, valid);
        const mdLinks = attachments.map(att => {
          const link = att.download_url || att.url;
          return `![${att.filename}](${link})`;
        }).join('\n');
        insertAtCursor(`\n\n${mdLinks}`);
      } else {
        const { pending, markdown } = placeholdersForPendingImages(valid);
        setPendingImages(prev => [...prev, ...pending]);
        insertAtCursor(`\n\n${markdown}`);
      }
    } catch (error) {
      console.error('Falha ao colar imagem do clipboard', error);
    }
  };

  const priorityOptions = {
    [TicketPriority.LOW]: t('tickets.priority.low'),
    [TicketPriority.MEDIUM]: t('tickets.priority.medium'),
    [TicketPriority.HIGH]: t('tickets.priority.high'),
    [TicketPriority.CRITICAL]: t('tickets.priority.critical'),
  };

  const categoryOptions = {
    [TicketCategory.SECURITY_INCIDENT]: t('tickets.category.security_incident'),
    [TicketCategory.FALSE_POSITIVE]: t('tickets.category.false_positive'),
    [TicketCategory.DATA_REQUEST]: t('tickets.category.data_request'),
    [TicketCategory.TECHNICAL_SUPPORT]: t('tickets.category.technical_support'),
    [TicketCategory.FEATURE_REQUEST]: t('tickets.category.feature_request'),
    [TicketCategory.OTHER]: t('tickets.category.other'),
  };

  const statusOptions = {
    [TicketStatus.OPEN]: t('tickets.status.open'),
    [TicketStatus.IN_PROGRESS]: t('tickets.status.in_progress'),
    [TicketStatus.WAITING_CLIENT]: t('tickets.status.waiting_client'),
    [TicketStatus.RESOLVED]: t('tickets.status.resolved'),
    [TicketStatus.CLOSED]: t('tickets.status.closed'),
  };

  const filteredUsers = users.filter(u => {
    const input = assignedInput.toLowerCase();
    return !input || u.email?.toLowerCase().includes(input) || u.full_name?.toLowerCase().includes(input);
  });

  return (
    <div className='p-6'>
      <div className='flex items-center mb-6'>
        <Button type='button' variant='ghost' onClick={onCancel} className='mr-2'>
          <ArrowLeftIcon className='h-4 w-4 mr-2' />
          {t('tickets.form.back')}
        </Button>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
          {isEditing ? `${t('tickets.form.titleEdit')} #${ticket.id}` : t('tickets.form.titleNew')}
        </h1>
      </div>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit} className='flex flex-col gap-5'>
            <div className='space-y-1.5'>
              <Label htmlFor='ticket-title'>{t('tickets.form.titleLabel')} *</Label>
              <Input
                id='ticket-title'
                value={formData.title}
                onChange={e => handleChange('title', e.target.value)}
              />
              {errors.title && <p className='text-xs text-danger-600'>{errors.title}</p>}
            </div>

            <div className='flex justify-end items-center gap-2'>
              <input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                className='hidden'
                onChange={handleFileSelected}
              />
              <Button type='button' size='sm' variant='outline' onClick={handleInsertImage}>
                {t('tickets.form.insertImage')}
              </Button>
              {attachmentError && (
                <Alert variant='destructive' className='py-2 px-3'>
                  <AlertDescription className='whitespace-pre-line'>{attachmentError}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='ticket-description'>{t('tickets.form.descriptionLabel')} *</Label>
              <Textarea
                id='ticket-description'
                rows={4}
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                onPaste={handleDescriptionPaste}
                ref={descriptionRef}
              />
              {errors.description && <p className='text-xs text-danger-600'>{errors.description}</p>}
              <p className='text-xs text-gray-500 dark:text-gray-400'>{t('tickets.form.markdownHelp')}</p>
            </div>

            <div className='flex gap-4 flex-wrap'>
              <div className='flex-1 min-w-[200px] space-y-1.5'>
                <Label>{t('tickets.table.priority')}</Label>
                <Select value={formData.priority} onValueChange={v => handleChange('priority', v)}>
                  <SelectTrigger>
                    <SelectValue>{priorityOptions[formData.priority]}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(priorityOptions).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='flex-1 min-w-[200px] space-y-1.5'>
                <Label>{t('tickets.table.category')}</Label>
                <Select value={formData.category} onValueChange={v => handleChange('category', v)}>
                  <SelectTrigger>
                    <SelectValue>{categoryOptions[formData.category]}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryOptions).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isEditing && (
                <div className='flex-1 min-w-[200px] space-y-1.5'>
                  <Label>{t('tickets.table.status')}</Label>
                  <Select value={formData.status} onValueChange={v => handleChange('status', v)}>
                    <SelectTrigger>
                      <SelectValue>{statusOptions[formData.status]}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusOptions).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className='flex-1 min-w-[300px] space-y-1.5' ref={assigneeBoxRef}>
                <Label htmlFor='ticket-assignee'>{t('tickets.form.assigneeByEmail')}</Label>
                {usersError && (
                  <Alert variant='destructive' className='py-2 px-3'>
                    <AlertDescription>{usersError}</AlertDescription>
                  </Alert>
                )}
                <div className='relative'>
                  <Input
                    id='ticket-assignee'
                    value={assignedInput}
                    placeholder={t('tickets.form.selectUserPlaceholder')}
                    onFocus={() => setAssigneeMenuOpen(true)}
                    onChange={e => {
                      setAssignedInput(e.target.value);
                      setAssigneeMenuOpen(true);
                      if (formData.assigned_to_id) handleChange('assigned_to_id', '');
                    }}
                  />
                  {assigneeMenuOpen && (
                    <div className='absolute top-full z-50 mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl shadow-lg shadow-black/5 max-h-60 overflow-auto p-1'>
                      {usersLoading && (
                        <div className='px-3 py-2 text-sm text-gray-500 dark:text-gray-400'>
                          <LoadingSpinner size='sm' className='inline-block mr-2' />
                          {t('common.loading', 'Carregando…')}
                        </div>
                      )}
                      {!usersLoading && filteredUsers.length === 0 && (
                        <div className='px-3 py-2 text-sm text-gray-500 dark:text-gray-400'>—</div>
                      )}
                      {filteredUsers.map(u => (
                        <div
                          key={u.id}
                          onClick={() => {
                            handleChange('assigned_to_id', u.id.toString());
                            setAssignedInput(u.full_name ? `${u.full_name} (${u.email})` : u.email);
                            setAssigneeMenuOpen(false);
                          }}
                          className='cursor-pointer px-3 py-2 text-sm rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-900 dark:text-gray-100'
                        >
                          {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className='text-xs text-gray-500 dark:text-gray-400'>{t('tickets.form.assigneeHelper')}</p>
              </div>

              {!isEditing && !isClientUser && (
                <div className='flex-1 min-w-[200px] space-y-1.5'>
                  <Label htmlFor='ticket-company-id'>{t('tickets.form.companyIdLabel')} *</Label>
                  <Input
                    id='ticket-company-id'
                    type='number'
                    value={formData.company_id}
                    onChange={e => handleChange('company_id', e.target.value)}
                  />
                  {errors.company_id && <p className='text-xs text-danger-600'>{errors.company_id}</p>}
                </div>
              )}
            </div>

            <div className='flex justify-end gap-2'>
              <Button type='button' variant='outline' onClick={onCancel} disabled={saving}>
                {t('tickets.view.actions.cancel')}
              </Button>
              <Button type='submit' disabled={saving}>
                {saving ? <LoadingSpinner size='sm' className='mr-2' /> : <ArrowDownOnSquareIcon className='h-4 w-4 mr-2' />}
                {saving ? t('tickets.view.actions.saving') : t('tickets.view.actions.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
